import React, { Fragment, useState } from 'react';
import i18n from '../../../i18n';
import './SubIndustry.css';
import { useSelector } from 'react-redux';
import Select, { components } from 'react-select';

const customStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    boxShadow: 'none',
    border: '1px solid #ccc',
    paddingLeft: '10px',
  }),
  option: (provided) => ({
    ...provided,
    color: '#5b6770',
    fontSize: 14,
    borderRadius: 0,
    backgroundColor: 'white',
    ':hover': {
      backgroundColor: '#050139',
      color: 'white',
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 0,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 100ms';
    return { ...provided, opacity, transition };
  },
  dropdownIndicator: (base) => ({
    ...base,
    color: '#050139',
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#050139',
    cursor: 'pointer',
  }),
};

const SubIndustry = () => {
  const data = useSelector((state) => state.fundData.data);
  let asOfDate = null;

  // DEFAULT row limit
  const [rowLimit, setRowLimit] = useState(15);

  // Multi-select chosen sub-industries
  const [selectedSubIndustries, setSelectedSubIndustries] = useState([]);
  // Typed search input (for “fallback” filtering when nothing is selected)
  const [searchTerm, setSearchTerm] = useState('');

  // Grab only the funds that have sub-exposures
  const funds = data.filter((x) => x.gicsIndustrySubExposures !== null);

  // 1. Create a map of sub-industry → exposure for the *first* fund
  let firstFundMap = {};
  if (funds[0] && funds[0].gicsIndustrySubExposures) {
    funds[0].gicsIndustrySubExposures.forEach((exposure) => {
      exposure.keys.forEach((subIndustry, i) => {
        firstFundMap[subIndustry] = exposure.values[i];
      });
    });
  }

  // 2. Gather all unique sub-industry keys
  const getUniqueKeys = () => {
    const allKeys = [];
    funds.forEach((fund) => {
      if (
        fund.gicsIndustrySubExposures &&
        fund.gicsIndustrySubExposures.length > 0
      ) {
        fund.gicsIndustrySubExposures.forEach((exposure) => {
          allKeys.push(...exposure.keys);
        });
      }
    });
    return [...new Set(allKeys)];
  };

  // 3. Sort those sub-industries by first-fund exposure desc
  const sortedUniqueKeys = getUniqueKeys().sort((a, b) => {
    const aVal = firstFundMap[a] || 0;
    const bVal = firstFundMap[b] || 0;
    return bVal - aVal; // Descending
  });

  // -------------------------------------------------------
  // *** Filtering logic: Let user selections override. ***
  // -------------------------------------------------------
  let filteredKeys;
  if (selectedSubIndustries.length > 0) {
    // If user explicitly selected some sub-industries,
    // show ONLY those (ignore searchTerm + row limit).
    filteredKeys = sortedUniqueKeys.filter((k) =>
      selectedSubIndustries.includes(k)
    );
  } else {
    // If NOTHING selected, then filter by typed searchTerm + row limit
    filteredKeys = sortedUniqueKeys.filter((key) =>
      key.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Then control how many rows to display
  const rowsToDisplay =
    selectedSubIndustries.length > 0
      ? filteredKeys // Show all if user selected some
      : filteredKeys.slice(0, rowLimit); // Otherwise apply rowLimit

  // Header
  const RenderHeader = () => {
    if (data[0].stdPerformanceMonthly) {
      asOfDate = new Date(
        data[0].stdPerformanceMonthly[0].asOfDate
      ).toLocaleDateString();
    }

    if (funds.length >= 1) {
      const tickerList = funds.map((fund, idx) => {
        const borderStyle = idx !== 0 ? 'holdingsBorderleft' : '';
        if (fund.gicsIndustrySubExposures) {
          return (
            <th
              key={fund.ticker}
              className={`${borderStyle} borderbottom textLeft`}
              style={{ textAlign: 'center' }}
            >
              {getFundLink(fund)}
            </th>
          );
        }
        return null;
      });

      return (
        <Fragment>
          <tr>
            <th className="textLeft" style={{ whiteSpace: 'nowrap' }}>
              {i18n.t('sub_industries').toUpperCase()} {i18n.t('as of')}{' '}
              {asOfDate}{' '}
            </th>

            <th className="borderRight"></th>
            {tickerList}
          </tr>
        </Fragment>
      );
    }
    return null;
  };

  // Fund link
  const getFundLink = (fund) => {
    if (
      fund.type === 'BMK' ||
      fund.type === 'BLEND' ||
      fund.type === 'COD' ||
      fund.type === 'WTMOD'
    ) {
      return (
        <span data-tip data-for={`_top_holdings_header`}>
          {fund.ticker.toUpperCase()}
        </span>
      );
    }
    const url = fund.fundPageURL || fund.msUrl;
    return (
      <a
        className="tickerLinkFC"
        href={url}
        target="_new"
        style={{ display: 'block', textAlign: 'center' }}
      >
        {fund.ticker}
      </a>
    );
  };

  // Format value
  const formatValue = (value) => {
    return value !== undefined && value !== null
      ? `${(value * 100).toFixed(2)}%`
      : '-';
  };

  // Colors for the mini bars
  const chartColors = [
    '#0047BB',
    '#050139',
    '#8C959B',
    '#F68D2E',
    '#65BD18',
    '#00A9E0',
  ];

  // Render rows
  const RenderRows = () => {
    return rowsToDisplay.map((key, idx) => {
      return (
        <tr key={idx}>
          <td
            className="textLeft"
            style={{ width: '20%', wordWrap: 'break-word' }}
          >
            {key}
          </td>
          <td className="textLeft" style={{ width: '30%' }}>
            <div
              style={{
                width: '100%',
                height: '40px',
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              {funds.map((fund, i) => {
                const valueForKey = fund.gicsIndustrySubExposures
                  ? fund.gicsIndustrySubExposures
                      .map((exposure) => {
                        const keyIndex = exposure.keys.indexOf(key);
                        return keyIndex !== -1
                          ? exposure.values[keyIndex]
                          : null;
                      })
                      .filter((value) => value !== null)[0]
                  : null;

                const barHeight = valueForKey
                  ? Math.min(valueForKey * 280, 30)
                  : 0;

                return (
                  <div
                    key={`${fund.ticker}-${key}`}
                    style={{
                      width: '20px',
                      height: `${barHeight}px`,
                      backgroundColor: chartColors[i],
                      margin: '0 2px',
                    }}
                  />
                );
              })}
            </div>
          </td>
          {funds.map((fund) => {
            const valueForKey = fund.gicsIndustrySubExposures
              ? fund.gicsIndustrySubExposures
                  .map((exposure) => {
                    const keyIndex = exposure.keys.indexOf(key);
                    return keyIndex !== -1 ? exposure.values[keyIndex] : null;
                  })
                  .filter((value) => value !== null)[0]
              : null;

            return (
              <td key={`${fund.ticker}-${key}`} className="textCenter">
                {formatValue(valueForKey)}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <div className="col-12 table-responsive">
      <div
        className="row"
        style={{
          paddingRight: '0px',
          marginBottom: '30px',
          paddingLeft: '0px',
        }}
      >
        <div className="col-lg-3 col-sm-6 mb-3">
          <Select
            isMulti
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
            hideSelectedOptions={false}
            options={sortedUniqueKeys.map((key) => ({
              value: key,
              label: key,
            }))}
            onChange={(selectedOptions, action) => {
              if (action.action === 'clear') {
                // User clicked the "X" to clear everything
                setSelectedSubIndustries([]);
                setSearchTerm(''); // Reset the searchTerm, too
              } else {
                const selectedValues = selectedOptions
                  ? selectedOptions.map((option) => option.value)
                  : [];
                setSelectedSubIndustries(selectedValues);
              }
            }}
            inputValue={searchTerm}
            onInputChange={(value, action) => {
              if (action.action === 'input-change') {
                setSearchTerm(value);
              }
            }}
            filterOption={(option, input) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            value={selectedSubIndustries.map((key) => ({
              value: key,
              label: key,
            }))}
            styles={customStyles}
            placeholder="Search or select sub-industries"
            isClearable
            components={{
              ClearIndicator: (props) => (
                <components.ClearIndicator {...props}>
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    ×
                  </span>
                </components.ClearIndicator>
              ),
            }}
          />
        </div>
      </div>

      <table className="table tableHR">
        <thead>{RenderHeader()}</thead>
        <tbody>{RenderRows()}</tbody>
      </table>

      {/* "Show More" button only if no explicit selection and we have more data */}
      {selectedSubIndustries.length === 0 && rowLimit < filteredKeys.length && (
        <div className="text-center">
          <button
            onClick={() => setRowLimit(rowLimit + 20)}
            className="btn btn-link"
          >
            {rowLimit < filteredKeys.length ? 'Click for More' : 'Show All'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SubIndustry;
